import React, {
  useEffect,
  useState,
  useRef
} from "react";
import {
  useNavigate
} from "react-router-dom";

import axios from "axios";
import html2pdf from "html2pdf.js";

import {
  Download,
  FileText,
  TrendingUp,
  Target,
  Trophy,
  Activity,
  Brain,
  Eye,
  Zap,
  Clock
} from "lucide-react";

import VisionNavbar from "./VisionNavbar";

import "./VisionIgniteReport.css";
import "./VisionPdf.css";




/* =========================================================
   ASSESSMENTS
========================================================= */

const assessmentConfig = [

  {
    key: "visualAcuity",
    label: "Visual Acuity",
    category: "visual",
    icon: Eye,
    aliases: [
      "visualAcuity",
      "visual_acuity",
      "visual acuity"
    ]
  },

  {
    key: "contrastSensitivity",
    label: "Contrast Sensitivity",
    category: "visual",
    icon: Target,
    aliases: [
      "contrastSensitivity",
      "contrast_sensitivity",
      "contrast sensitivity"
    ]
  },

  {
    key: "simpleReaction",
    label: "Simple Reaction",
    category: "reaction",
    icon: Zap,
    aliases: [
      "simpleReaction",
      "simple_reaction",
      "simple reaction",
      "reactionSpeed",
      "reaction_speed"
    ]
  },

  {
    key: "complexReaction",
    label: "Complex Reaction",
    category: "reaction",
    icon: Activity,
    aliases: [
      "complexReaction",
      "complex_reaction",
      "complex reaction"
    ]
  },

  {
    key: "shortTermMemory",
    label: "Short-Term Memory",
    category: "cognitive",
    icon: Brain,
    aliases: [
      "shortTermMemory",
      "short_term_memory",
      "short-term memory",
      "short memory"
    ]
  },

  {
    key: "workingMemory",
    label: "Working Memory",
    category: "cognitive",
    icon: Brain,
    aliases: [
      "workingMemory",
      "working_memory",
      "working memory",
      "memoryLevel"
    ]
  },

  {
    key: "searchSpan",
    label: "Search Span",
    category: "attention",
    icon: Eye,
    aliases: [
      "searchSpan",
      "search_span",
      "search span"
    ]
  },

  {
    key: "anticipation",
    label: "Anticipation",
    category: "attention",
    icon: Target,
    aliases: [
      "anticipation",
      "anticipationTest",
      "anticipation_test",
      "anticipationScore"
    ]
  }

];


/* =========================================================
   NORMALIZE
========================================================= */

const normalizeKey = (value) => {

  return String(value || "")
    .toLowerCase()
    .replace(
      /[^a-z0-9]/g,
      ""
    );

};


/* =========================================================
   RAW METRICS
========================================================= */

const getRawMetrics = (report) => {

  if (
    Array.isArray(
      report?.assessments
    )
  ) {
    return report.assessments;
  }

  if (
    Array.isArray(
      report?.results
    )
  ) {
    return report.results;
  }

  if (
    Array.isArray(
      report?.metrics
    )
  ) {
    return report.metrics;
  }

  return (
    report?.metrics ||
    report?.assessments ||
    report?.results ||
    {}
  );

};


/* =========================================================
   FIND ASSESSMENT
========================================================= */

const findAssessmentData = (
  rawMetrics,
  config
) => {

  if (
    Array.isArray(
      rawMetrics
    )
  ) {

    return (
      rawMetrics.find(
        item => {

          const name =
            item?.key ||
            item?.name ||
            item?.test ||
            item?.assessment ||
            item?.title ||
            "";

          const normalized =
            normalizeKey(name);

          return config.aliases.some(
            alias =>
              normalizeKey(alias) ===
              normalized
          );

        }
      ) || null
    );

  }


  if (
    rawMetrics &&
    typeof rawMetrics === "object"
  ) {

    for (
      const [
        key,
        value
      ]
      of Object.entries(
        rawMetrics
      )
    ) {

      const normalized =
        normalizeKey(key);

      const matched =
        config.aliases.some(
          alias =>
            normalizeKey(alias) ===
            normalized
        );

      if (matched) {
        return value;
      }

    }

  }

  return null;

};


/* =========================================================
   GET SCORE
========================================================= */

const getScore = (item) => {

  if (
    item === null ||
    item === undefined
  ) {
    return null;
  }

  if (
    typeof item === "number"
  ) {
    return item;
  }

  if (
    typeof item === "string"
  ) {

    const number =
      Number(item);

    return Number.isFinite(
      number
    )
      ? number
      : null;

  }

  if (
    typeof item === "object"
  ) {

    const values = [

      item.percentile,
      item.score,
      item.value,
      item.result,
      item.percentage,
      item.finalNumber,
      item.accuracy

    ];

    for (
      const value
      of values
    ) {

      if (
        value !== null &&
        value !== undefined &&
        value !== ""
      ) {

        const number =
          Number(value);

        if (
          Number.isFinite(
            number
          )
        ) {
          return number;
        }

      }

    }

  }

  return null;

};


/* =========================================================
   MAIN
========================================================= */

const Report = () => {

  const email =
    localStorage.getItem(
      "user_email"
    );
    const navigate = useNavigate();

 useEffect(() => {

    const navbar =
      document.querySelector(
        "header, .navbar"
      );

    const footer =
      document.querySelector(
        "footer"
      );


    if (navbar)
      navbar.style.display = "none";

    if (footer)
      footer.style.display = "none";


    return () => {

      if (navbar)
        navbar.style.display = "";

      if (footer)
        footer.style.display = "";

    };

  }, []);

  const [
    report,
    setReport
  ] = useState(null);


  const [
    level,
    setLevel
  ] = useState(
    Number(
      localStorage.getItem(
        "user_level"
      )
    ) || 1
  );


  const [
    profilePhoto,
    setProfilePhoto
  ] = useState(
    localStorage.getItem(
      "profile_photo"
    ) || ""
  );


  const [
    loading,
    setLoading
  ] = useState(true);


  const [
    exporting,
    setExporting
  ] = useState(false);


  const [
    error,
    setError
  ] = useState("");


  const [
    showAll,
    setShowAll
  ] = useState(false);


  const pdfRef =
    useRef(null);


  /* =====================================================
     LEVEL NAMES
  ===================================================== */

  const levelNames = {

    1: "AWARE",
    2: "EXPLORER",
    3: "ADAPTER",
    4: "THRIVER"

  };


  /* =====================================================
     LOAD PROFILE / LEVEL
  ===================================================== */

  useEffect(() => {

    if (!email) {
      return;
    }

    loadProfile();

  }, [email]);


  const loadProfile = async () => {

    try {

      const response =
        await axios.get(
          `/api/ignite/profile/${encodeURIComponent(
            email
          )}`
        );


      const data =
        response.data;


      const user =
        data?.user ||
        data?.profile ||
        data;


      const currentLevel =
        Number(
          user?.level ||
          data?.level ||
          data?.summary?.currentLevel ||
          1
        );


      setLevel(
        currentLevel
      );


      localStorage.setItem(
        "user_level",
        String(currentLevel)
      );


      const photo =
        user?.profilePhoto ||
        data?.profilePhoto ||
        "";


      if (photo) {

        setProfilePhoto(
          photo
        );

        localStorage.setItem(
          "profile_photo",
          photo
        );

      }

    }

    catch (err) {

      console.error(
        "Profile load error:",
        err
      );

    }

  };


  /* =====================================================
     LOAD REPORT
  ===================================================== */

  useEffect(() => {

    if (!email) {

      setError(
        "User email not found."
      );

      setLoading(false);

      return;

    }

    fetchReport();

  }, [email]);


  const fetchReport = async () => {

    try {

      setLoading(true);

      setError("");


      const response =
        await axios.get(
          `/get-report?email=${encodeURIComponent(
            email
          )}`
        );


      setReport(
        response.data
      );

    }

    catch (err) {

      console.error(
        "Report Error:",
        err
      );

      setError(
        err.response?.data?.message ||
        "Unable to load report."
      );

    }

    finally {

      setLoading(false);

    }

  };


  /* =====================================================
     METRICS
  ===================================================== */

  const rawMetrics =
    getRawMetrics(
      report
    );


  const allAssessments =
    assessmentConfig.map(
      config => {

        const data =
          findAssessmentData(
            rawMetrics,
            config
          );

        const score =
          getScore(data);


        return {

          ...config,

          data,

          score,

          available:
            score !== null

        };

      }
    );


  const availableAssessments =
    allAssessments.filter(
      item =>
        item.available
    );


  const visibleAssessments =
    showAll
      ? allAssessments
      : availableAssessments.slice(
          0,
          9
        );


  const averagePercentile =
    availableAssessments.length
      ? Math.round(
          availableAssessments.reduce(
            (
              total,
              item
            ) =>
              total +
              item.score,
            0
          ) /
          availableAssessments.length
        )
      : 0;


  const bestScore =
    availableAssessments.length
      ? Math.max(
          ...availableAssessments.map(
            item =>
              item.score
          )
        )
      : 0;


  /* =====================================================
     CATEGORY COUNT
  ===================================================== */

  const visualCount =
    availableAssessments.filter(
      item =>
        item.category ===
        "visual"
    ).length;


  const reactionCount =
    availableAssessments.filter(
      item =>
        item.category ===
        "reaction"
    ).length;


  const cognitiveCount =
    availableAssessments.filter(
      item =>
        item.category ===
        "cognitive"
    ).length;


  const attentionCount =
    availableAssessments.filter(
      item =>
        item.category ===
        "attention"
    ).length;


  /* =====================================================
     PDF ROW HELPERS (mirrors ReportPage.jsx layout)
  ===================================================== */

  const getByKey = (key) =>
    allAssessments.find(
      (item) => item.key === key
    );

  const displayValue = (item) => {

    if (!item || !item.available) {
      return "-";
    }

    const raw =
      item.data?.value ??
      item.data?.result ??
      item.data?.score ??
      item.score;

    return typeof raw === "number"
      ? raw.toFixed(2)
      : raw;

  };

  const displayPercentile = (item) =>
    item && item.available
      ? `${item.score.toFixed(2)}%`
      : "-";

  const basicRows = [
    getByKey("visualAcuity"),
    getByKey("contrastSensitivity")
  ].filter((item) => item && item.available);

  const processingRows = [
    getByKey("simpleReaction"),
    getByKey("complexReaction")
  ].filter((item) => item && item.available);

  const executiveRows = [
    getByKey("shortTermMemory"),
    getByKey("workingMemory"),
    getByKey("searchSpan"),
    getByKey("anticipation")
  ].filter((item) => item && item.available);

  const definitionText = {

    visualAcuity: {
      what: "How clearly one can see small details at 6 m (both eyes).",
      read: "The higher the better (6/6 = 1.0; 6/4.5 ≈ 1.33).",
      why: "Sharp visual clarity supports faster reactions, accurate targeting, and precise execution under pressure. It’s the foundation for reading the game early, coordinating efficiently."
    },

    contrastSensitivity: {
      what: "Ability to detect objects against low contrast backgrounds.",
      read: "Lower value indicates better sensitivity (can detect finer contrast differences).",
      why: "Critical in low light, fog, or fast-moving environments where clear visibility is reduced. Helps in early detection of objects, opponents, and movement."
    },

    simpleReaction: {
      what: "Time taken to respond to a sudden light cue.",
      read: "Reported in milliseconds. Lower score implies quicker reactions.",
      why: "Supports paying attention to cues, timely starts, and fewer slip-ups."
    },

    complexReaction: {
      what: "How quickly an athlete reacts to unpredictable or multi-choice stimuli.",
      read: "Reported in milliseconds. Lower score implies quicker reactions.",
      why: "Reflects processing speed, focus, and adaptability under pressure. Essential in fast-paced sports where milliseconds decide performance."
    },

    shortTermMemory: {
      what: "How well one remembers numbers and recites them back in order.",
      read: "Longest number string said correctly. Higher implies stronger short-term memory.",
      why: "Supports strategy recall, quick calculations, and multi-step execution."
    },

    workingMemory: {
      what: "How well one remembers numbers and recites them back in order.",
      read: "Longest number string said correctly. Higher implies stronger short-term memory.",
      why: "Supports strategy recall, quick calculations, and multi-step execution."
    },

    searchSpan: {
      what: "How fast one can find the right items when the clock is ticking.",
      read: "More correct finds implies better search-and-find ability.",
      why: "Key for reading the game, spotting opportunities, and reacting fast."
    },

    anticipation: {
      what: "Anticipation of motion.",
      read: "Percent correct, higher implies better prediction and timing.",
      why: "Crucial for reading motion and reacting early, from catching and intercepting to crossing, passing, or positioning accurately in dynamic environments."
    }

  };


  /* =====================================================
     EXPORT PDF
  ===================================================== */

  const handleExportReport =
    async () => {

      if (
        exporting ||
        !email ||
        !report
      ) {
        return;
      }


      try {

        setExporting(true);

        setError("");


        await new Promise(
          resolve =>
            requestAnimationFrame(
              () =>
                requestAnimationFrame(
                  resolve
                )
            )
        );


        if (
          !pdfRef.current
        ) {

          throw new Error(
            "Unable to prepare report."
          );

        }


        const athleteName =
          report?.name ||
          report?.athleteName ||
          "Athlete";


        const safeName =
          athleteName
            .replace(
              /[^a-z0-9]/gi,
              "_"
            );


        const options = {

          margin: 0,

          filename:
            `${safeName}_Vision_Ignite_Report.pdf`,

          image: {

            type: "jpeg",

            quality: 1

          },

          html2canvas: {

            scale: 2,

            useCORS: true,

            scrollY: 0

          },

          jsPDF: {

            unit: "mm",

            format: "a4",

            orientation:
              "portrait"

          },

          pagebreak: {

            mode: [
              "css",
              "legacy"
            ]

          }

        };


        await html2pdf()
          .set(options)
          .from(
            pdfRef.current
          )
          .save();


        try {

          await axios.post(
            "/track-download",
            {

              email:
                report?.email ||
                email,

              reportId:
                report?.reportId ||
                "",

              action:
                "download",

              downloadedAt:
                new Date()

            }
          );

        }

        catch (trackingError) {

          console.error(
            "Tracking error:",
            trackingError
          );

        }

      }

      catch (err) {

        console.error(
          "Export error:",
          err
        );

        setError(
          err.message ||
          "Unable to download report."
        );

      }

      finally {

        setExporting(false);

      }

    };


  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {

    return (

      <div className="vision-report-page">

        <VisionNavbar />

        <main className="vision-report-main">

          <div className="vision-report-loading">

            Loading your Vision Ignite report...

          </div>

        </main>

      </div>

    );

  }


  return (

    <div className="vision-report-page">

      <VisionNavbar />


      <main className="vision-report-main">


        {/* =================================================
            HEADER
        ================================================= */}

        <section className="vision-report-header">

          <div>

            <span className="report-eyebrow">
              VISION IGNITE
            </span>

            <h1>
              Performance Report
            </h1>

            <p>
              Track your visual and cognitive
              performance journey.
            </p>

          </div>


          <div className="report-header-right">

            <div className="report-level-pill">

              LEVEL {level} :
              {" "}
              {levelNames[level]}

            </div>


            <div className="report-user">

              <img
                src={
                  profilePhoto ||
                  "/Hero/profile.png"
                }
                alt="Profile"
              />

              <div>

                <strong>
                  {
                    report?.name ||
                    report?.athleteName ||
                    "Athlete"
                  }
                </strong>

                <span>
                  {levelNames[level]}
                </span>

              </div>

            </div>

          </div>

        </section>


        {/* =================================================
            ACTION
        ================================================= */}

        <div className="vision-report-actions">
          
          <button
            className="vision-leaderboard-btn"
            onClick={() => navigate("/leaderboard")}
        >
            Leaderboard
        </button>
          <button
            className="vision-export-btn"
            onClick={
              handleExportReport
            }
            disabled={
              exporting ||
              !report
            }
          >

            <Download
              size={17}
            />

            {
              exporting
                ? "Preparing..."
                : "Export Report"
            }

          </button>

        </div>


        {error && (

          <div className="vision-report-error">

            {error}

          </div>

        )}


        {/* =================================================
            STAT CARDS
        ================================================= */}

        <section className="vision-stat-grid">


          <div className="vision-stat-card">

            <div className="vision-stat-icon blue">

              <Activity
                size={21}
              />

            </div>

            <span>
              Assessments
            </span>

            <strong>
              9
            </strong>

            <small>
              Metrics completed
            </small>

          </div>


          <div className="vision-stat-card">

            <div className="vision-stat-icon pink">

              <Target
                size={21}
              />

            </div>

            <span>
              Average Score
            </span>

            <strong>
              {averagePercentile}%
            </strong>

            <small>
              Overall performance
            </small>

          </div>


          <div className="vision-stat-card">

            <div className="vision-stat-icon green">

              <TrendingUp
                size={21}
              />

            </div>

            <span>
              Best Score
            </span>

            <strong>
              {Math.round(bestScore)}%
            </strong>

            <small>
              Highest metric
            </small>

          </div>


          <div className="vision-stat-card">

            <div className="vision-stat-icon orange">

              <Trophy
                size={21}
              />

            </div>

            <span>
              Current Level
            </span>

            <strong>
              {level}
            </strong>

            <small>
              {levelNames[level]}
            </small>

          </div>

        </section>


        {/* =================================================
            MAIN GRID
        ================================================= */}

        <section className="vision-report-grid">


          {/* =================================================
              PERFORMANCE
          ================================================= */}

          <div className="vision-panel">

            <div className="vision-panel-header">

              <div>

                <span className="panel-eyebrow">
                  PERFORMANCE
                </span>

                <h2>
                  Performance Overview
                </h2>

                <p>
                  Your latest Vision Ignite
                  assessment results.
                </p>

              </div>


              <button
                className="vision-view-btn"
                onClick={() =>
                  setShowAll(
                    !showAll
                  )
                }
              >

                {
                  showAll
                    ? "Show Less"
                    : "View All"
                }

              </button>

            </div>


            <div className="vision-performance-list">

              {visibleAssessments.map(
                item => {

                  const Icon =
                    item.icon;


                  return (

                    <div
                      className="vision-performance-item"
                      key={
                        item.key
                      }
                    >

                      <div className="vision-performance-top">

                        <span>

                          <span className="metric-icon">

                            <Icon
                              size={16}
                            />

                          </span>

                          {item.label}

                        </span>


                        <strong>

                          {
                            item.available
                              ? `${item.score.toFixed(2)}%`
                              : "Not completed"
                          }

                        </strong>

                      </div>


                      <div className="vision-performance-bar">

                        <div
                          className="vision-performance-fill"
                          style={{
                            width:
                              item.available
                                ? `${Math.min(
                                    Math.max(
                                      item.score,
                                      0
                                    ),
                                    100
                                  )}%`
                                : "0%"
                          }}
                        />

                      </div>

                    </div>

                  );

                }
              )}

            </div>

          </div>


          {/* =================================================
              CATEGORY
          ================================================= */}

          <div className="vision-panel">

            <div className="vision-panel-header">

              <div>

                <span className="panel-eyebrow">
                  ANALYSIS
                </span>

                <h2>
                  Performance Categories
                </h2>

                <p>
                  Assessment distribution.
                </p>

              </div>

            </div>


            <div className="vision-category-list">


              <div className="vision-category-card">

                <div className="vision-category-icon blue">

                  <Eye
                    size={19}
                  />

                </div>

                <div>

                  <strong>
                    Visual Functions
                  </strong>

                  <span>
                    {visualCount} metrics
                  </span>

                </div>

              </div>


              <div className="vision-category-card">

                <div className="vision-category-icon pink">

                  <Zap
                    size={19}
                  />

                </div>

                <div>

                  <strong>
                    Processing Speed
                  </strong>

                  <span>
                    {reactionCount} metrics
                  </span>

                </div>

              </div>


              <div className="vision-category-card">

                <div className="vision-category-icon purple">

                  <Brain
                    size={19}
                  />

                </div>

                <div>

                  <strong>
                    Cognitive Functions
                  </strong>

                  <span>
                    {cognitiveCount} metrics
                  </span>

                </div>

              </div>


              <div className="vision-category-card">

                <div className="vision-category-icon orange">

                  <Target
                    size={19}
                  />

                </div>

                <div>

                  <strong>
                    Attention & Anticipation
                  </strong>

                  <span>
                    {attentionCount} metrics
                  </span>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =================================================
            BOTTOM
        ================================================= */}

        <section className="vision-report-grid bottom">


          {/* =================================================
              TABLE
          ================================================= */}

          <div className="vision-panel">

            <div className="vision-panel-header">

              <div>

                <span className="panel-eyebrow">
                  RESULTS
                </span>

                <h2>
                  Recent Assessments
                </h2>

                <p>
                  Detailed assessment results.
                </p>

              </div>


              <button
                className="vision-view-btn"
                onClick={() =>
                  setShowAll(
                    !showAll
                  )
                }
              >

                {
                  showAll
                    ? "Show Less"
                    : "View All"
                }

              </button>

            </div>


            <div className="vision-table-wrapper">

              <table className="vision-table">

                <thead>

                  <tr>

                    <th>
                      Assessment
                    </th>

                    <th>
                      Result
                    </th>

                    <th>
                      Score
                    </th>

                  </tr>

                </thead>


                <tbody>

                  {visibleAssessments.map(
                    item => {

                      const Icon =
                        item.icon;


                      return (

                        <tr
                          key={
                            item.key
                          }
                        >

                          <td>

                            <div className="vision-table-name">

                              <span>

                                <Icon
                                  size={15}
                                />

                              </span>

                              {item.label}

                            </div>

                          </td>


                          <td>

                            {
                              item.available
                                ? item.data?.value ??
                                  item.data?.result ??
                                  item.data?.score ??
                                  "-"
                                : "-"
                            }

                          </td>


                          <td>

                            <span
                              className={
                                item.available
                                  ? "vision-score-pill"
                                  : "vision-score-pill neutral"
                              }
                            >

                              {
                                item.available
                                  ? `${item.score.toFixed(2)}%`
                                  : "Not completed"
                              }

                            </span>

                          </td>

                        </tr>

                      );

                    }
                  )}

                </tbody>

              </table>

            </div>

          </div>


          {/* =================================================
              INSIGHTS
          ================================================= */}

          <div className="vision-panel">

            <div className="vision-panel-header">

              <div>

                <span className="panel-eyebrow">
                  INSIGHTS
                </span>

                <h2>
                  Insights & Summary
                </h2>

                <p>
                  Your current performance.
                </p>

              </div>

            </div>


            <div className="vision-insights">


              <div className="vision-insight">

                <div className="vision-insight-icon green">

                  <TrendingUp
                    size={17}
                  />

                </div>

                <div>

                  <strong>
                    Assessment Coverage
                  </strong>

                  <p>
                    {availableAssessments.length}
                    {" "}
                    assessment metrics
                    are available.
                  </p>

                </div>

              </div>


              <div className="vision-insight">

                <div className="vision-insight-icon blue">

                  <Clock
                    size={17}
                  />

                </div>

                <div>

                  <strong>
                    Average Performance
                  </strong>

                  <p>
                    Current average:
                    {" "}
                    {averagePercentile}%.
                  </p>

                </div>

              </div>


              <div className="vision-insight">

                <div className="vision-insight-icon pink">

                  <Trophy
                    size={17}
                  />

                </div>

                <div>

                  <strong>
                    Vision Ignite Level
                  </strong>

                  <p>
                    You are currently at
                    Level {level} —
                    {" "}
                    {levelNames[level]}.
                  </p>

                </div>

              </div>


              <div className="vision-insight">

                <div className="vision-insight-icon orange">

                  <FileText
                    size={17}
                  />

                </div>

                <div>

                  <strong>
                    Report Ready
                  </strong>

                  <p>
                    Your latest performance
                    report is available.
                  </p>

                </div>

              </div>

            </div>

          </div>

        </section>


      </main>


      {/* =================================================
          HIDDEN PDF — mirrors ReportPage.jsx layout,
          texture and colors exactly
      ================================================= */}

      <div
        className="vision-hidden-pdf"
        aria-hidden="true"
      >

        {report && (

          <div
            ref={pdfRef}
            className="report"
          >

            {/* HEADER */}
            <div className="headerRow">

              <div className="logo">
                <img
                  src="/Logo.png"
                  alt="Forge Your Peak"
                  className="brand-logo"
                />
              </div>

              <div className="company">
                <h2>Forge Your Peak Pvt Ltd</h2>
                <p>Assessment Report</p>
              </div>

              <div className="contact">
                <p>support@forgeyourpeak.in</p>
                <p>+91 8056023798</p>
                <p>https://forgeyourpeak.in/</p>
              </div>

            </div>

            {/* INFO TABLE */}
            <table className="infoTable">
              <tbody>
                <tr>
                  <td>
                    Name:{" "}
                    {report?.name ||
                      report?.athleteName ||
                      "-"}
                  </td>
                  <td>
                    Athlete Id: {report?.athleteId || "-"}
                  </td>
                </tr>

                <tr>
                  <td>
                    Email: {report?.email || email || "-"}
                  </td>
                  <td>
                    Report Id: {report?.reportId || "-"}
                  </td>
                </tr>

                <tr>
                  <td>
                    Test Type: {report?.testType || "Vision Ignite"}
                  </td>
                  <td>
                    Collection Date:{" "}
                    {report?.collectionDate ||
                      new Date().toLocaleDateString()}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* MAIN TABLE */}
            <table className="mainTable">

              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Sub-Metric</th>
                  <th>Value</th>
                  <th>Percentile</th>
                </tr>
              </thead>

              <tbody>

                {/* BASIC */}
                {basicRows.length > 0 &&
                  basicRows.map((row, i) => (
                    <tr key={row.key}>
                      {i === 0 && (
                        <td
                          className="orange"
                          rowSpan={basicRows.length}
                        >
                          Basic Visual Functions
                        </td>
                      )}
                      <td>{row.label}</td>
                      <td>{displayValue(row)}</td>
                      <td>{displayPercentile(row)}</td>
                    </tr>
                  ))}

                {/* PROCESSING */}
                {processingRows.length > 0 &&
                  processingRows.map((row, i) => (
                    <tr key={row.key}>
                      {i === 0 && (
                        <td
                          className="blue"
                          rowSpan={processingRows.length}
                        >
                          Visual Processing Speed
                        </td>
                      )}
                      <td>{row.label}</td>
                      <td>{displayValue(row)}</td>
                      <td>{displayPercentile(row)}</td>
                    </tr>
                  ))}

                {/* EXECUTIVE */}
                {executiveRows.length > 0 &&
                  executiveRows.map((row, i) => (
                    <tr key={row.key}>
                      {i === 0 && (
                        <td
                          className="green"
                          rowSpan={executiveRows.length}
                        >
                          Visual Executive Functions
                        </td>
                      )}
                      <td>{row.label}</td>
                      <td>{displayValue(row)}</td>
                      <td>{displayPercentile(row)}</td>
                    </tr>
                  ))}

              </tbody>
            </table>

            {/* PYRAMID */}
            <div className="pyramidSection">

              <h3>Testing Pyramid</h3>

              <div className="pyramidWrap">

                <div className="pyramid">
                  <div className="layer top">Visual Executive Functions</div>
                  <div className="layer middle">Visual Processing Speed</div>
                  <div className="layer bottom">Basic Visual Functions</div>
                </div>

                <div className="desc">

                  <div className="descItem greenLine">
                    <h4>Visual Executive Functions</h4>
                    <p>Attention, working memory, anticipation</p>
                  </div>

                  <div className="descItem blueLine">
                    <h4>Visual Processing Speed</h4>
                    <p>Reaction speed</p>
                  </div>

                  <div className="descItem orangeLine">
                    <h4>Basic Visual Functions</h4>
                    <p>Clarity & contrast</p>
                  </div>

                </div>

              </div>

            </div>

            <div className="page-break"></div>

            {/* SECOND PAGE — DEFINITIONS */}
            <div className="secondPage">

              <div className="pdfTopSpace"></div>

              <h2 className="title">Test Definitions & Details</h2>

              <table className="bigTable">

                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Sub-Metric</th>
                    <th>Definition and Details</th>
                  </tr>
                </thead>

                <tbody>

                  {/* BASIC */}
                  {basicRows.map((row, i) => (
                    <tr key={row.key}>
                      {i === 0 && (
                        <td
                          className="orangeMain"
                          rowSpan={basicRows.length}
                        >
                          Basic<br />Visual<br />Functions
                        </td>
                      )}
                      <td className="orangeSub">{row.label}</td>
                      <td>
                        <b>What it measures:</b>{" "}
                        {definitionText[row.key]?.what}
                        <br />
                        <b>How to read:</b>{" "}
                        {definitionText[row.key]?.read}
                        <br />
                        <b>Why it matters:</b>{" "}
                        {definitionText[row.key]?.why}
                      </td>
                    </tr>
                  ))}

                  {/* PROCESSING */}
                  {processingRows.map((row, i) => (
                    <tr key={row.key}>
                      {i === 0 && (
                        <td
                          className="blueMain"
                          rowSpan={processingRows.length}
                        >
                          Visual<br />Processing<br />Speed
                        </td>
                      )}
                      <td className="blueSub">{row.label}</td>
                      <td>
                        <b>What it measures:</b>{" "}
                        {definitionText[row.key]?.what}
                        <br />
                        <b>How to read:</b>{" "}
                        {definitionText[row.key]?.read}
                        <br />
                        <b>Why it matters:</b>{" "}
                        {definitionText[row.key]?.why}
                      </td>
                    </tr>
                  ))}

                  {/* EXECUTIVE */}
                  {executiveRows.map((row, i) => (
                    <tr key={row.key}>
                      {i === 0 && (
                        <td
                          className="greenMain"
                          rowSpan={executiveRows.length}
                        >
                          Visual<br />Executive<br />Functions
                        </td>
                      )}
                      <td className="greenSub">{row.label}</td>
                      <td>
                        <b>What it measures:</b>{" "}
                        {definitionText[row.key]?.what}
                        <br />
                        <b>How to read:</b>{" "}
                        {definitionText[row.key]?.read}
                        <br />
                        <b>Why it matters:</b>{" "}
                        {definitionText[row.key]?.why}
                      </td>
                    </tr>
                  ))}

                </tbody>
              </table>

            </div>

          </div>

        )}

      </div>

    </div>

  );

};


export default Report;
